import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FaSpinner } from 'react-icons/fa';
import { ArrowUpCircle } from 'lucide-react';

export interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<string>;
  remainingDays: number;
  amount: number;
  isLoading: boolean;
}

export function UpgradeDialog({
  open,
  onOpenChange,
  onConfirm,
  remainingDays,
  amount,
  isLoading
}: UpgradeDialogProps) {
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    try {
      setError(null);
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : '업그레이드 중 오류가 발생했습니다.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden rounded-xl">
        {/* 헤더 영역 - 배경색 추가 */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-5 border-b">
          <div className="flex items-center gap-3 mb-1">
            <ArrowUpCircle className="h-6 w-6 text-purple-500" />
            <DialogTitle className="text-xl text-gray-800">프리미엄 플랜으로 업그레이드</DialogTitle>
          </div>
          <DialogDescription className="text-gray-600 mt-2">
            더 많은 기능과 무제한 사용량을 이용해보세요
          </DialogDescription>
        </div>
        
        {/* 콘텐츠 영역 */}
        <div className="px-6 py-5">
          {/* 금액 강조 표시 */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-5">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">업그레이드 금액</span>
              <span className="text-lg font-bold text-purple-700">{amount.toLocaleString()}원</span>
            </div>
          </div>
          
          {/* 혜택 설명 */}
          <div className="space-y-3 mb-6">
            <h4 className="font-medium text-gray-700">업그레이드 혜택</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-purple-500 mr-2">•</span>
                <span>무제한 기능 사용</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-500 mr-2">•</span>
                <span>다음 결제일까지 남은 기간({remainingDays}일)에 대한 추가요금만 결제됩니다.</span>
              </li>
            </ul>
          </div>
          
          {/* 안내 메시지 */}
          <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
            확인 버튼을 누르면 바로 결제되며 프리미엄 플랜으로 업그레이드됩니다.
          </div>
          
          {error && (
            <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-100">
              {error}
            </div>
          )}
        </div>

        {/* 버튼 영역 */}
        <DialogFooter className="px-6 py-4 bg-gray-50 border-t flex flex-row justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            취소
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="min-w-[100px] bg-purple-600 hover:bg-purple-700 text-white"
          >
            {isLoading ? (
              <>
                <FaSpinner className="mr-2 h-4 w-4 animate-spin" />
                처리 중...
              </>
            ) : '확인'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 